import { useRoute } from 'shared/library/routes/route';
import React, { useMemo, useState } from 'react';
import { Platform, View } from 'react-native';
import { Props, Breed } from 'shared/@types';
import { useTranslation } from 'react-i18next';
import { apiRoute } from 'shared/library/api-route';
import { H1 } from 'shared/components/title';
import { ComparisonCard } from 'shared/components/comparison-card';
import { PageBuilder } from 'shared/library/page-builder';
import { Breadcrumb } from 'shared/components/breadcrumb';
import { Text } from 'shared/components/text';
import { PageContent } from 'shared/components/page-content';
import { compactBreedForComparison, ComparisonCardBreed } from 'shared/library/compact-page-data';
import { falSearch } from 'shared/@generated/icons/falSearch';
import { Button } from 'shared/components/button';
import { TextInput } from 'shared/components/form/text-input';
import { Icon } from 'shared/components/icon';

const COMPARISONS_PER_PAGE = 24;

function normalizeBreedSearch(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

interface IndexBreedComparisonsScreenProps extends Props {
  breed: ComparisonCardBreed;
  comparables: ComparisonCardBreed[];
}

const IndexBreedComparisonsScreen = ({ breed, comparables }: IndexBreedComparisonsScreenProps) => {
  const { t } = useTranslation('screens');
  const route = useRoute();
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(COMPARISONS_PER_PAGE);
  const filteredComparables = useMemo(() => {
    const normalizedQuery = normalizeBreedSearch(query);

    return comparables.filter((comparable) =>
      normalizeBreedSearch(comparable.name).includes(normalizedQuery),
    );
  }, [comparables, query]);

  return (
    <PageContent nativeSafeTop={true}>
      <Breadcrumb
        className={'mb-1.5'}
        items={[
          { text: t('breeds.show_breed.breeds'), href: route('breeds.index') },
          { text: breed.name, href: route('breeds.show', { breed: breed.slug }) },
          { text: t('breeds.index_comparisons.breadcrumb'), href: null },
        ]}
      />

      <H1
        title={t('breeds.index_comparisons.title', { breed: breed.name })}
        subtitle={t('breeds.index_comparisons.description', { breed: breed.name })}
        hideTitleWhenNativeHeader={true}
      />

      {comparables.length > 0 && (
        <View className={'mb-4 gap-3 md:mb-6'}>
          <View
            className={
              'border-brand-brown-dark/15 flex-row items-center gap-3 rounded border bg-white px-3'
            }
          >
            <Icon icon={falSearch} className={'text-brand-brown-dark/50 size-4'} />
            <TextInput
              testID={'breed-comparisons-search'}
              accessibilityLabel={t('breeds.index_comparisons.search_placeholder')}
              placeholder={t('breeds.index_comparisons.search_placeholder')}
              value={query}
              unstyled={true}
              className={'min-w-0 flex-1 py-3 text-base'}
              autoCapitalize={'none'}
              autoCorrect={false}
              onChangeText={(value) => {
                setQuery(value);
                setVisibleCount(COMPARISONS_PER_PAGE);
              }}
            />
          </View>
          <Text className={'text-brand-brown-dark/70 text-sm'} accessibilityLiveRegion={'polite'}>
            {t('breeds.index_comparisons.results_count', { count: filteredComparables.length })}
          </Text>
        </View>
      )}

      {comparables.length === 0 ? (
        <View className={'border-brand-brown-dark/15 rounded-lg border bg-white p-6'}>
          <Text className={'text-brand-brown-dark/70 text-center'}>
            {t('breeds.index_comparisons.no_comparisons')}
          </Text>
        </View>
      ) : filteredComparables.length === 0 ? (
        <View className={'border-brand-brown-dark/15 rounded-lg border bg-white p-6'}>
          <Text className={'text-brand-brown-dark text-center font-bold'}>
            {t('breeds.index_comparisons.no_search_results', { query: query.trim() })}
          </Text>
          <Text className={'text-brand-brown-dark/70 mt-2 text-center'}>
            {t('breeds.index_comparisons.try_another_search')}
          </Text>
        </View>
      ) : (
        <View
          className={
            Platform.OS === 'web'
              ? 'flex-cols-1 md:flex-cols-2 lg:flex-cols-3 flex-row gap-3 md:gap-6'
              : 'gap-3'
          }
        >
          {filteredComparables.slice(0, visibleCount).map((comparable) => (
            <View key={comparable.id}>
              <ComparisonCard breed1={breed} breed2={comparable} />
            </View>
          ))}
        </View>
      )}

      {filteredComparables.length > visibleCount && (
        <View className={'mt-6 items-center'}>
          <Button
            testID={'breed-comparisons-show-more'}
            text={t('breeds.index_comparisons.show_more')}
            color={'white'}
            center={true}
            onPress={() => setVisibleCount((count) => count + COMPARISONS_PER_PAGE)}
          />
        </View>
      )}
    </PageContent>
  );
};

IndexBreedComparisonsScreen.pageBuilder = new PageBuilder<
  IndexBreedComparisonsScreenProps,
  { breed: string }
>()
  .request(({ req, params }) =>
    Promise.all([
      req<{ breed: Breed }>(apiRoute('breeds.show', { breed: params.breed })),
      req<{ breeds: Breed[] }>(apiRoute('breeds.index')),
    ]).then(([breedData, breedsData]) => {
      const breed = breedData.breed;
      const allBreeds = breedsData.breeds;

      const comparables = allBreeds
        .filter((otherBreed) => otherBreed.animal === breed.animal && otherBreed.id !== breed.id)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(compactBreedForComparison);

      return { breed: compactBreedForComparison(breed), comparables };
    }),
  )
  .deriveRouteParams((props) => ({
    breed: props.breed.slug,
  }));

export { IndexBreedComparisonsScreen };
export type { IndexBreedComparisonsScreenProps };
